import { useCallback, useEffect, useState } from "react";

import { useT } from "../../../shared/i18n";
import { Banner, Button, CorrelationRef, Skeleton } from "../../../shared/ui";
import { ErrorRetry } from "../ErrorRetry";
import { OperatorDenied, OperatorRefused } from "../operatorHttp";
import { PermissionDenied } from "../PermissionDenied";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import { TraceTimeline } from "./TraceTimeline";
import type { TraceApi, TraceDetailData } from "./traceApi";

const FULL_USER_ID = /^[0-9a-fA-F]{8}-[0-9a-fA-F-]{27}$/;

type State =
  | { kind: "loading" }
  | { kind: "failed" }
  | { kind: "notFound" }
  | { kind: "denied" }
  | { kind: "ready"; data: TraceDetailData };

/** One call with Prev/Next to the neighbouring calls of the same session. */
export function TraceDetail({
  api,
  correlationId,
  onNavigate,
  onBack,
  onOpenUser,
}: {
  api: TraceApi;
  correlationId: string;
  onNavigate: (correlationId: string) => void;
  /** Single-pane back to the list; omitted when the list is beside it. */
  onBack?: () => void;
  /** Opens the user route when a record carries a full (unmasked) user id. */
  onOpenUser?: (userId: string) => void;
}) {
  const t = useT();
  const reportExpired = useReportSessionExpired();
  const [state, setState] = useState<State>({ kind: "loading" });

  const run = useCallback(() => {
    api
      .detail(correlationId)
      .then((data) => {
        setState({ kind: "ready", data });
      })
      .catch((error: unknown) => {
        if (isSessionExpired(error)) reportExpired();
        if (error instanceof OperatorDenied) {
          setState({ kind: "denied" });
        } else if (error instanceof OperatorRefused && error.status === 404) {
          setState({ kind: "notFound" });
        } else {
          setState({ kind: "failed" });
        }
      });
  }, [api, correlationId, reportExpired]);

  const load = () => {
    setState({ kind: "loading" });
    run();
  };

  useEffect(() => {
    run();
  }, [run]);

  const related = state.kind === "ready" ? state.data.related : null;
  const userId =
    state.kind === "ready"
      ? (state.data.records
          .map((record) => record.user_id)
          .find((id) => id !== null && FULL_USER_ID.test(id)) ?? null)
      : null;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {onBack && (
          <Button variant="ghost" size="sm" onClick={onBack}>
            {t("admin.logs.back")}
          </Button>
        )}
        <Button
          variant="secondary"
          size="sm"
          disabled={!related?.previous}
          onClick={() => {
            if (related?.previous) onNavigate(related.previous);
          }}
        >
          {t("admin.logs.previous")}
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!related?.next}
          onClick={() => {
            if (related?.next) onNavigate(related.next);
          }}
        >
          {t("admin.logs.next")}
        </Button>
        {onOpenUser && userId !== null && (
          <Button
            variant="tinted"
            size="sm"
            onClick={() => {
              onOpenUser(userId);
            }}
          >
            {t("admin.logs.openUser")}
          </Button>
        )}
      </div>
      <CorrelationRef
        correlationId={correlationId}
        label={t("admin.logs.detail.callId")}
        copyLabel={t("admin.logs.detail.copy")}
        copiedLabel={t("admin.logs.detail.copied")}
      />
      {state.kind === "loading" && (
        <div aria-busy="true">
          <Skeleton height={96} />
          <span className="sr-only">{t("admin.logs.detail.loading")}</span>
        </div>
      )}
      {state.kind === "failed" && (
        <ErrorRetry message={t("admin.logs.detail.failed")} onRetry={load} />
      )}
      {state.kind === "notFound" && (
        <Banner tone="info">{t("admin.logs.detail.notFound")}</Banner>
      )}
      {state.kind === "denied" && (
        <PermissionDenied message={t("admin.denied.logs")} />
      )}
      {state.kind === "ready" && state.data.degraded === "unavailable" && (
        <Banner tone="warning">{t("admin.logs.unavailable")}</Banner>
      )}
      {state.kind === "ready" && state.data.degraded === "none" && (
        <TraceTimeline records={state.data.records} />
      )}
    </div>
  );
}
